/**
 * DndProvider — wraps @dnd-kit/core DndContext for the test-cases module.
 *
 * Draggable: TestCaseRow (drag handle) + FolderTreeNode (folder drag handle).
 * Droppable: FolderTreeNode (highlights on active drag hover).
 *
 * onDragEnd receives { activeId, overId, activeType }.
 *  - activeType 'testCase' → move test cases to the target folder
 *  - activeType 'folder'   → nest the source folder inside the target folder
 *
 * Fix: snapCenterToCursor modifier is applied to the DragOverlay so the
 * ghost pill snaps directly under the cursor instead of appearing at the
 * top-left corner of the dragged row.
 */

import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent, Modifier } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { useState } from 'react';

export interface DragEndPayload {
    activeId:     string;
    overId:       string | null;
    activeType:   'testCase' | 'folder';
    /** 'testCase' when dropped on a list row; 'folder' when dropped on a folder node. */
    overType:     'testCase' | 'folder' | null;
    /** Only meaningful when overType === 'testCase'. */
    dropPosition: 'before' | 'after' | null;
}

export interface DragOverPayload {
    overId:       string | null;
    overType:     'testCase' | 'folder' | null;
    dropPosition: 'before' | 'after' | null;
}

interface DndProviderProps {
    children:    React.ReactNode;
    onDragEnd:   (payload: DragEndPayload) => void;
    /** Called on every pointer-move while over a droppable — used to render insertion lines. */
    onDragOver?: (payload: DragOverPayload) => void;
}

/**
 * Snaps the DragOverlay so that the exact point the user clicked on the
 * draggable element aligns with the top-left of the floating ghost.
 *
 * The previous implementation had the offset sign inverted, which caused the
 * overlay to appear far away from the cursor. The corrected formula is:
 *   adjustX = cursorX - elementLeft
 *   adjustY = cursorY - elementTop
 * This shifts the overlay start position so the cursor sits at its top-left
 * corner — natural and predictable for a small pill-style ghost.
 */
const snapCursorToOverlay: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
    if (draggingNodeRect && activatorEvent) {
        const coords = getEventCoordinates(activatorEvent);
        if (!coords) return transform;
        return {
            ...transform,
            x: transform.x + (coords.x - draggingNodeRect.left),
            y: transform.y + (coords.y - draggingNodeRect.top),
        };
    }
    return transform;
};

/** Compute 'before' | 'after' from the active element's translated rect vs. the over element's midpoint. */
function resolveDropPosition(event: DragEndEvent | DragOverEvent): 'before' | 'after' {
    if (!event.over) return 'after';
    const overMidY = event.over.rect.top + event.over.rect.height / 2;
    const translated = event.active.rect.current.translated;
    if (!translated) return 'after';
    const activeCenterY = translated.top + translated.height / 2;
    return activeCenterY <= overMidY ? 'before' : 'after';
}

export function DndProvider({ children, onDragEnd, onDragOver }: DndProviderProps) {
    const [activeLabel, setActiveLabel] = useState<string | null>(null);

    // PointerSensor: small activation distance so row clicks still work.
    // TouchSensor: 250ms delay + 5px tolerance so it doesn't block native scroll.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor,   { activationConstraint: { delay: 250, tolerance: 5 } }),
    );

    function handleDragStart(event: DragStartEvent) {
        setActiveLabel(String(event.active.data.current?.label ?? event.active.id));
    }

    function handleDragOver(event: DragOverEvent) {
        if (!onDragOver) return;
        if (!event.over || String(event.over.id) === String(event.active.id)) {
            onDragOver({ overId: null, overType: null, dropPosition: null });
            return;
        }
        const overType = (event.over.data.current?.type ?? 'testCase') as 'testCase' | 'folder';
        const dropPosition = overType === 'testCase' ? resolveDropPosition(event) : null;
        onDragOver({ overId: String(event.over.id), overType, dropPosition });
    }

    function handleDragEnd(event: DragEndEvent) {
        setActiveLabel(null);
        // Clear the insertion-line state before the caller processes the drop.
        if (onDragOver) onDragOver({ overId: null, overType: null, dropPosition: null });

        const rawType = event.active.data.current?.type;
        const activeType: DragEndPayload['activeType'] = rawType === 'folder' ? 'folder' : 'testCase';
        const overType = event.over
            ? ((event.over.data.current?.type ?? 'testCase') as 'testCase' | 'folder')
            : null;
        const dropPosition = overType === 'testCase' ? resolveDropPosition(event) : null;

        onDragEnd({
            activeId:     String(event.active.id),
            overId:       event.over ? String(event.over.id) : null,
            activeType,
            overType,
            dropPosition,
        });
    }

    function handleDragCancel() {
        setActiveLabel(null);
        if (onDragOver) onDragOver({ overId: null, overType: null, dropPosition: null });
    }

    return (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            {children}

            {/* Floating ghost label while dragging */}
            <DragOverlay dropAnimation={null} modifiers={[snapCursorToOverlay]}>
                {activeLabel ? (
                    <div className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg shadow-xl opacity-90 pointer-events-none whitespace-nowrap">
                        {activeLabel}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
